import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Building2,
  Target,
  Users,
  BarChart3,
  Settings,
  Search,
  Trophy,
  ArrowRight,
  LogOut,
  HardHat,
  Calendar,
  FileText,
  Wrench,
  Lock,
  Loader2,
  ShieldCheck
} from 'lucide-react';

const PortalSelector = ({ companyName, onSelect, showSwitch = false }) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [licenceTravauxActive, setLicenceTravauxActive] = useState(false);
  const [loadingLicence, setLoadingLicence] = useState(true);
  const [activating, setActivating] = useState(false);

  // Vérifier si l'utilisateur peut accéder au portail Travaux
  // Seuls les admins et super_admins peuvent y accéder
  const canAccessTravaux = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  // Vérifier le statut de la licence Travaux
  useEffect(() => {
    const checkLicence = async () => {
      if (!canAccessTravaux) {
        setLoadingLicence(false);
        return;
      }
      try {
        const response = await api.getTravauxLicenceStatus();
        setLicenceTravauxActive(response.data?.active || false);
      } catch (error) {
        console.error('Erreur vérification licence travaux:', error);
        setLicenceTravauxActive(false);
      } finally {
        setLoadingLicence(false);
      }
    };
    checkLicence();
  }, [canAccessTravaux]);

  // Forcer l'activation de la licence (super_admin uniquement)
  const handleForceActivate = async () => {
    if (!user?.company_id) {
      toast.error('Company ID non trouvé');
      return;
    }
    
    setActivating(true);
    try {
      const response = await api.forceActivateTravauxLicence(user.company_id);
      toast.success(response.data?.message || 'Licence Travaux activée !');
      setLicenceTravauxActive(true);
    } catch (error) {
      console.error('Erreur activation licence:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActivating(false);
    }
  };

  const handleSelect = (portal) => {
    if (onSelect) {
      onSelect(portal);
    }
    if (portal === 'admin') {
      navigate('/admin');
    } else if (portal === 'travaux') {
      navigate('/travaux');
    } else {
      navigate('/agent');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`${showSwitch ? '' : 'fixed inset-0 z-[9998]'} bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4`}>
      <div className="w-full max-w-5xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">CoproEnergy</h1>
          </div>
          {companyName && (
            <p className="text-slate-400 text-lg">{companyName}</p>
          )}
          <p className="text-slate-500 mt-2">Choisissez votre espace de travail</p>
        </div>

        {/* Portal Cards - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Admin Portal */}
          <Card 
            className="bg-slate-800/50 border-slate-700 hover:border-blue-500 transition-all cursor-pointer group"
            onClick={() => handleSelect('admin')}
            data-testid="portal-admin"
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-blue-600/20 rounded-xl flex items-center justify-center group-hover:bg-blue-600/30 transition flex-shrink-0">
                  <Settings className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1">Portail Admin</h3>
                  <p className="text-slate-400 text-xs mb-3">
                    Pilotage commercial & équipes
                  </p>
                  
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-slate-300 text-xs">
                      <Users className="w-3.5 h-3.5 text-blue-400" />
                      Gestion des agents
                    </div>
                    <div className="flex items-center gap-2 text-slate-300 text-xs">
                      <BarChart3 className="w-3.5 h-3.5 text-blue-400" />
                      Tableau de bord & KPIs
                    </div>
                    <div className="flex items-center gap-2 text-slate-300 text-xs">
                      <Target className="w-3.5 h-3.5 text-blue-400" />
                      Pipeline opportunités
                    </div>
                  </div>
                </div>
              </div>
              
              <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700 group-hover:bg-blue-500 h-9 text-sm">
                Espace Admin
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Travaux Portal - Visible uniquement pour les admins avec licence */}
          {canAccessTravaux && (
            <Card 
              className={`bg-slate-800/50 border-slate-700 transition-all cursor-pointer group relative overflow-hidden ${
                licenceTravauxActive ? 'hover:border-amber-500' : 'opacity-60'
              }`}
              onClick={() => licenceTravauxActive && handleSelect('travaux')}
              data-testid="portal-travaux"
            >
              {/* Badge ACTIF ou VERROUILLÉ */}
              <div className="absolute top-3 right-3">
                {licenceTravauxActive ? (
                  <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    ACTIF
                  </span>
                ) : (
                  <span className="bg-slate-600 text-slate-300 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    NON ACTIVÉ
                  </span>
                )}
              </div>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition flex-shrink-0 ${
                    licenceTravauxActive 
                      ? 'bg-amber-600/20 group-hover:bg-amber-600/30' 
                      : 'bg-slate-700/50'
                  }`}>
                    <HardHat className={`w-6 h-6 ${licenceTravauxActive ? 'text-amber-400' : 'text-slate-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-white mb-1">Portail Travaux</h3>
                    <p className="text-slate-400 text-xs mb-3">
                      Gestion des chantiers
                    </p>
                    
                    <div className="space-y-1.5">
                      <div className={`flex items-center gap-2 text-xs ${licenceTravauxActive ? 'text-slate-300' : 'text-slate-500'}`}>
                        <Wrench className={`w-3.5 h-3.5 ${licenceTravauxActive ? 'text-amber-400' : 'text-slate-600'}`} />
                        Suivi des chantiers
                      </div>
                      <div className={`flex items-center gap-2 text-xs ${licenceTravauxActive ? 'text-slate-300' : 'text-slate-500'}`}>
                        <Calendar className={`w-3.5 h-3.5 ${licenceTravauxActive ? 'text-amber-400' : 'text-slate-600'}`} />
                        Planning & interventions
                      </div>
                      <div className={`flex items-center gap-2 text-xs ${licenceTravauxActive ? 'text-slate-300' : 'text-slate-500'}`}>
                        <FileText className={`w-3.5 h-3.5 ${licenceTravauxActive ? 'text-amber-400' : 'text-slate-600'}`} />
                        Devis & facturation
                      </div>
                    </div>
                  </div>
                </div>
                
                <Button 
                  className={`w-full mt-4 h-9 text-sm ${
                    licenceTravauxActive 
                      ? 'bg-amber-600 hover:bg-amber-700 group-hover:bg-amber-500' 
                      : 'bg-slate-700 hover:bg-slate-600'
                  }`}
                  disabled={!licenceTravauxActive && !isSuperAdmin}
                  onClick={licenceTravauxActive ? undefined : (isSuperAdmin ? handleForceActivate : undefined)}
                >
                  {licenceTravauxActive ? (
                    <>
                      Espace Travaux
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  ) : activating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Activation...
                    </>
                  ) : isSuperAdmin ? (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Forcer activation
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Activer la licence
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Agent Portal */}
          <Card 
            className="bg-slate-800/50 border-slate-700 hover:border-emerald-500 transition-all cursor-pointer group"
            onClick={() => handleSelect('agent')}
            data-testid="portal-agent"
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center group-hover:bg-emerald-600/30 transition flex-shrink-0">
                  <Target className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white mb-1">Portail Agent</h3>
                  <p className="text-slate-400 text-xs mb-3">
                    Prospection terrain
                  </p>
                  
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-slate-300 text-xs">
                      <Search className="w-3.5 h-3.5 text-emerald-400" />
                      Recherche copropriétés
                    </div>
                    <div className="flex items-center gap-2 text-slate-300 text-xs">
                      <Target className="w-3.5 h-3.5 text-emerald-400" />
                      Ma prospection
                    </div>
                    <div className="flex items-center gap-2 text-slate-300 text-xs">
                      <Trophy className="w-3.5 h-3.5 text-emerald-400" />
                      Gamification
                    </div>
                  </div>
                </div>
              </div>
              
              <Button className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 group-hover:bg-emerald-500 h-9 text-sm">
                Espace Agent
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer hint */}
        <p className="text-center text-slate-500 text-sm mt-6">
          Vous pourrez basculer entre les portails à tout moment depuis le menu
        </p>

        {/* Logout Button */}
        <div className="text-center mt-4">
          <Button 
            variant="ghost" 
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
            data-testid="portal-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Se déconnecter
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PortalSelector;
